import React, { useState, useRef, useEffect } from 'react';
import { ConstructorEventUpdateSQLStatus } from '../../../../Event/event';

const BarGraphBuilder = () => {
  const maxVal = 10;
  const graphHeight = 300;
  const bottomPadding = 0;
  const maxBarHeight = graphHeight - bottomPadding;

  const [dataInputs, setDataInputs] = useState([{ label: '', value: '' }]);
  const [graphData, setGraphData] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [draggingIndex, setDraggingIndex] = useState(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const getRandomColor = () => {
    const h = Math.floor(Math.random() * 360);
    return `hsl(${h}, 70%, 70%)`;
  };

  const handleAddRow = () => {
    setDataInputs([...dataInputs, { label: '', value: '' }]);
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...dataInputs];
    updated[index][field] = value;
    setDataInputs(updated);
  };

  const buildGraph = () => {
    const validData = [];
    for (let i = 0; i < dataInputs.length; i++) {
      const label = dataInputs[i].label.trim();
      const value = parseInt(dataInputs[i].value, 10);
      if (!label) {
        alert(`Label #${i + 1} is empty.`);
        return;
      }
      if (isNaN(value) || value < 0 || value > maxVal) {
        alert(`Value #${i + 1} must be between 0 and ${maxVal}.`);
        return;
      }
      // Set initial height to 0
      validData.push({ label, value, color: getRandomColor(), height: 0 });
    }
    setGraphData(validData);
    setFeedback('');
  };

  const handlePointerDown = (e, index) => {
    const rect = e.target.getBoundingClientRect();
    startYRef.current = e.clientY;
    startHeightRef.current = graphData[index].height;
    setDraggingIndex(index);
  };

  const handlePointerMove = (e) => {
    if (draggingIndex === null) return;
    const deltaY = startYRef.current - e.clientY;
    let newHeight = Math.min(Math.max(startHeightRef.current + deltaY, 0), maxBarHeight);

    const updated = [...graphData];
    updated[draggingIndex].height = newHeight;
    setGraphData(updated);
  };

  const handlePointerUp = () => {
    setDraggingIndex(null);
  };

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [draggingIndex, graphData]);

  const handleKeyDown = (e, index) => {
    if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const updated = [...graphData];
    let value = Math.round((updated[index].height / maxBarHeight) * maxVal);
    value = e.key === 'ArrowUp' ? Math.min(value + 1, maxVal) : Math.max(value - 1, 0);
    updated[index].height = (value / maxVal) * maxBarHeight;
    setGraphData(updated);
  };

  const handleCheck = () => {
    const correct = graphData.every((item, i) => {
      const expectedValue = parseInt(dataInputs[i].value, 10);
      const actualValue = Math.round((item.height / maxBarHeight) * maxVal);
      return actualValue === expectedValue;
    });

    setFeedback(
      correct
        ? '🎉 Great job! Your bar graph is correct!'
        : 'Oops! Some bars don’t match the data. Try again!'
    );
    if(correct){
         const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
    }
  };

  return (
    <div style={{ fontFamily: 'Comic Neue, cursive', background: '#f0f8ff', padding: '20px' }}>
      <div style={containerStyle}>
        <h2>📊 Create Your Dataset</h2>
        <p>Enter labels and values for your dataset below. Then click "Build Graph" to create the bar graph activity for kids.</p>
        {dataInputs.map((row, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <input
              type="text"
              placeholder="Label (e.g., Cats)"
              value={row.label}
              onChange={(e) => handleInputChange(index, 'label', e.target.value)}
              style={textInputStyle}
            />
            <input
              type="number"
              placeholder="Value (0-10)"
              min="0"
              max="10"
              value={row.value}
              onChange={(e) => handleInputChange(index, 'value', e.target.value)}
              style={numberInputStyle}
            />
          </div>
        ))}
        <button style={buttonStyle} onClick={handleAddRow}>+ Add More</button>
        <button style={buttonStyle} onClick={buildGraph}>Build Graph</button>
      </div>

      {graphData.length > 0 && (
        <div style={containerStyle}>
          <h2>🧮 Build the Bar Graph</h2>
          <p>Drag the bars up and down to match the values you entered. Press "Check Graph" to see if you are correct.</p>
          <div style={graphAreaStyle}>
            <div style={yAxisStyle}>
              {[10, 8, 6, 4, 2, 0].map((num) => (
                <div key={num}>{num}</div>
              ))}
            </div>
            {graphData.map((item, i) => {
              const currentValue = Math.round((item.height / maxBarHeight) * maxVal);
              return (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div
                    role="slider"
                    tabIndex={0}
                    aria-valuemin={0}
                    aria-valuemax={10}
                    aria-valuenow={currentValue}
                    aria-label={`${item.label} bar, current value ${currentValue}`}
                    style={{
                      ...barStyle,
                      height: `${item.height}px`,
                      background: `linear-gradient(135deg, ${item.color}, #1f4e7a)`,
                    }}
                    onPointerDown={(e) => handlePointerDown(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                  >
                    <div style={barValueStyle}>{currentValue}</div>
                  </div>
                  <div style={barLabelStyle}>{item.label}</div>
                </div>
              );
            })}
          </div>
          <button style={buttonStyle} onClick={handleCheck}>Check Graph</button>

{feedback && (
  <div
    style={{
      ...feedbackStyle,
      color: feedback.includes('Great') ? '#2e7d32' : '#c62828'
    }}
  >
    {feedback}
  </div>
)}

{feedback.includes('Great') && (
  <button style={buttonStyle} onClick={() => {
    // Reset everything for a fresh question
    setDataInputs([{ label: '', value: '' }]);
    setGraphData([]);
    setFeedback('');
  }}>
    ➡️ Next Question
  </button>
)}

          {/* <button style={buttonStyle} onClick={handleCheck}>Check Graph</button> */}
          {/* <div style={{ ...feedbackStyle, color: feedback.includes('Great') ? '#2e7d32' : '#c62828' }}>{feedback}</div> */}
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  maxWidth: '700px',
  margin: '0 auto 40px',
  background: '#ffffffdd',
  padding: '20px',
  borderRadius: '15px',
  boxShadow: '0 8px 20px rgba(30, 70, 110, 0.2)',
};

const textInputStyle = {
  flex: 2,
  padding: '8px 10px',
  fontSize: '1rem',
  borderRadius: '8px',
  border: '1.5px solid #2a5d9f',
  width: '100%',
};

const numberInputStyle = {
  flex: 1,
  padding: '8px 10px',
  fontSize: '1rem',
  borderRadius: '8px',
  border: '1.5px solid #2a5d9f',
};

const buttonStyle = {
  display: 'block',
  margin: '20px auto 0 auto',
  background: '#357abd',
  color: 'white',
  border: 'none',
  padding: '12px 30px',
  fontWeight: 700,
  fontSize: '1.1rem',
  borderRadius: '15px',
  cursor: 'pointer',
  boxShadow: '0 5px 15px rgba(53, 122, 189, 0.4)',
};

const graphAreaStyle = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-around',
  height: '300px',
  borderLeft: '3px solid #2a5d9f',
  borderBottom: '3px solid #2a5d9f',
  paddingBottom: '0px',
  position: 'relative',
};

const barStyle = {
  width: '60px',
  borderRadius: '10px 10px 0 0',
  cursor: 'grab',
  position: 'relative',
  userSelect: 'none',
};

const barValueStyle = {
  position: 'absolute',
  top: '-25px',
  width: '100%',
  textAlign: 'center',
  fontWeight: 700,
  color: '#154273',
  userSelect: 'none',
};

const barLabelStyle = {
  marginTop: '8px',
  fontWeight: 700,
  color: '#1f4e7a',
  userSelect: 'none',
};

const yAxisStyle = {
  position: 'absolute',
  left: '-35px',
  bottom: '25px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '300px',
  fontWeight: 700,
  color: '#2a5d9f',
  fontSize: '0.85rem',
  userSelect: 'none',
};

const feedbackStyle = {
  marginTop: '15px',
  fontWeight: 700,
  textAlign: 'center',
  fontSize: '1.2rem',
};

export default BarGraphBuilder;
